// © Microsoft Corporation. All rights reserved.

import {
    accentFillActiveBehavior,
    accentFillHoverBehavior,
    accentFillRestBehavior,
    neutralFillActiveBehavior,
    neutralFillFocusBehavior,
    neutralFillHoverBehavior,
    neutralFillRestBehavior,
    neutralFocusBehavior,
    neutralForegroundActiveBehavior,
    neutralForegroundHintBehavior,
    neutralForegroundHoverBehavior,
    neutralForegroundRestBehavior,
    neutralOutlineRestBehavior
} from "@fluentui/web-components";
import { display, focusVisible, forcedColorsStylesheetBehavior } from "@microsoft/fast-foundation";

import { css } from "@microsoft/fast-element";
import { SystemColors } from "@microsoft/fast-web-utilities";

export const brandingStyles = css`
    .control {
        color: var(--brand-nav-item-color);
        fill: var(--brand-nav-item-color);
        border: 1px solid transparent;
    }

    :host([active]) .control {
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
        font-weight: 600;
    }

    :host(.brand-nav) .control:hover,
    :host(.brand-nav) .control {
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
        font-weight: 700;
        border: 1px solid transparent;
        display: block;
    }

    :host(.brand-nav) .control:${focusVisible},
    .control:${focusVisible} {
        outline: none;
        border: 1px solid ${neutralFocusBehavior.var};
        border-radius: 2px;
    }

    :host([active]) .control:hover, 
    .control:hover {
        color: ${neutralForegroundHoverBehavior.var};
        fill: ${neutralForegroundHoverBehavior.var};
        border: 1px solid transparent;
    }
    
    ::slotted([slot="start"]) {
        padding-inline-end: 0px;
        line-height: normal;
    }`
    .withBehaviors(
        neutralForegroundRestBehavior,
        neutralForegroundHintBehavior,
        neutralForegroundHoverBehavior,
        neutralFocusBehavior
    );

export const navItemStyles = css`
    ${display("flex")} :host {
        font-family: var(--body-font);
        outline: none;
    }

    .control {
        font-size: var(--type-ramp-base-font-size);
        line-height: var(--type-ramp-base-line-height);
        box-sizing: border-box;
        justify-content: center;
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        outline: none;
        text-decoration: none;
        cursor: pointer;
        color: ${neutralForegroundRestBehavior.var};
        fill: ${neutralForegroundRestBehavior.var};
        border-bottom: 2px solid transparent;
    }

    :host([active]) .control {
        color: ${neutralForegroundActiveBehavior.var};
        fill: ${neutralForegroundActiveBehavior.var};
        font-weight: 700;
    }

    .control:hover {
        border-bottom-color: ${neutralFillHoverBehavior.var};
    }
    
    .control:${focusVisible} {
        outline: none;
        border-bottom-color: ${neutralFocusBehavior.var};
    }

    .control::-moz-focus-inner {
        border: 0;
    }

    :host(.disabled) {
        opacity: var(--disabled-opacity);
    }

    ::slotted([slot="start"]) {
        vertical-align: middle;
        padding-inline-end: 4px;
        line-height: var(--type-ramp-minus-1-line-height);
    }`
    .withBehaviors(
        accentFillActiveBehavior,
        accentFillHoverBehavior,
        accentFillRestBehavior,
        neutralFillActiveBehavior,
        neutralFillFocusBehavior,
        neutralFillHoverBehavior,
        neutralFillRestBehavior,
        neutralFocusBehavior,
        neutralForegroundRestBehavior,
        neutralOutlineRestBehavior,
        forcedColorsStylesheetBehavior(
            css`
            .control {
                border-bottom-color: ${SystemColors.ButtonFace};
                color: ${SystemColors.LinkText};
                fill: currentColor;
            }

            :host([active]) .control {
                color: ${SystemColors.LinkText};
                fill: currentColor;
            }
        `)
    );
